import { useCallback, useState } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { attachJob } from '@/features/ai/jobsSlice'
import { useStartTranslationMutation } from '@/features/ai/studioApi'
import { parseStartError, type StartFailure } from '@/features/ai/lib/parseStartError'

/**
 * Запуск перевода курса. Отдельный хук, а не ветка соседей: другое тело,
 * другой result и, главное, ЦЕПОЧКА — после success бэк сам ставит джобу на
 * картинки (§7.4), её подхватывает useTranslationChain.
 *
 * `target_lang` всегда null: язык выводится как «реестр минус sourceLang»,
 * выбора в UI нет намеренно.
 *
 * `translateImages` — БЕЗ дефолта: забыть его должно ломать сборку, а не молча
 * слать дефолт сервера. Цена этого решения — явный третий аргумент во всех
 * вызовах; она меньше, чем цена расхождения показанной цены с отправленной.
 *
 * `imagesTotal` — ожидаемое число картинок из сметы (только когда картинки
 * входят в прогон): общая полоса держит его в знаменателе, пока картиночная
 * джоба не назвала своё `images_total`. Необязательный — без него полоса
 * просто не знает про картинки заранее, как раньше.
 *
 * `start` возвращает отказ (или `null` при успехе), а не только кладёт его в
 * стейт: старт из диалога идёт при закрытом меню, и отказ надо показать тостом
 * сразу же.
 */
export const useStartTranslation = () => {
  const dispatch = useAppDispatch()
  const [startTranslation, { isLoading }] = useStartTranslationMutation()
  const [failure, setFailure] = useState<StartFailure | null>(null)

  const start = useCallback(
    async (
      courseId: number,
      overwrite: boolean,
      translateImages: boolean,
      imagesTotal?: number,
    ): Promise<StartFailure | null> => {
      setFailure(null)
      try {
        const started = await startTranslation({
          course_id: courseId,
          target_lang: null,
          overwrite,
          translate_images: translateImages,
        }).unwrap()
        dispatch(
          attachJob({
            courseId,
            entry: {
              jobId: started.job_id,
              type: 'course_translation',
              startedAt: Date.now(),
              params: { kind: 'course_translation', overwrite },
              total: started.units_total ?? null,
              ...(imagesTotal ? { imagesTotal } : {}),
            },
          }),
        )
        return null
      } catch (error) {
        const parsed = parseStartError(error)
        setFailure(parsed)
        return parsed
      }
    },
    [startTranslation, dispatch],
  )

  return { start, isStarting: isLoading, failure, reset: () => setFailure(null) }
}
